/**
 * TipTap / ProseMirror stores blank lines as empty <p> / <p><br></p>.
 * Email clients (and Tailwind preflight in in-app previews) collapse those
 * tags, so the sent message looks cramped compared to the editor.
 * Inline styles + &nbsp; keep one visible blank line per empty paragraph.
 */

const BLOCK_STYLE: Record<string, string> = {
  p: 'margin:0;padding:0;line-height:1.5;',
  h1: 'margin:16px 0 8px 0;padding:0;line-height:1.3;',
  h2: 'margin:14px 0 8px 0;padding:0;line-height:1.3;',
  h3: 'margin:12px 0 8px 0;padding:0;line-height:1.3;',
  ul: 'margin:8px 0;padding-left:24px;',
  ol: 'margin:8px 0;padding-left:24px;',
  li: 'margin:0 0 4px 0;',
}

const EMPTY_INNER = /^(?:\s|&nbsp;|&#160;|<br\b[^>]*>)*$/i
const BLOCK_TAGS = Object.keys(BLOCK_STYLE).join('|')
const BLOCK_OPEN = new RegExp(`<(${BLOCK_TAGS})(\\s[^>]*)?>`, 'gi')
const PARAGRAPH = /<p(\s[^>]*)?>([\s\S]*?)<\/p>/gi

const mergeInlineStyle = (existing: string, extra: string) => {
  let result = existing.trim()
  if (result && !result.endsWith(';'))
    result += ';'

  extra.split(';').map(part => part.trim()).filter(Boolean).forEach((declaration) => {
    const key = declaration.split(':')[0]?.trim().toLowerCase()
    if (!key)
      return
    if (new RegExp(`(?:^|;)\\s*${key}\\s*:`, 'i').test(existing))
      return
    result += `${declaration};`
  })

  return result
}

const upsertStyle = (openTag: string, extra: string) => {
  if (/style\s*=/i.test(openTag)) {
    return openTag.replace(/style\s*=\s*(["'])([\s\S]*?)\1/i, (_match, quote: string, existing: string) => {
      return `style=${quote}${mergeInlineStyle(existing, extra)}${quote}`
    })
  }

  return openTag.replace(/>$/, ` style="${extra}">`)
}

const isEmptyParagraphInner = (inner: string) => EMPTY_INNER.test(inner)

export const prepareEditorHtmlForEmail = (html: string) => {
  if (typeof html !== 'string' || !html)
    return html || ''

  const withBlankLines = html.replace(PARAGRAPH, (_match, attrs = '', inner: string) => {
    const body = isEmptyParagraphInner(inner) ? '&nbsp;' : inner
    return `<p${attrs}>${body}</p>`
  })

  return withBlankLines.replace(BLOCK_OPEN, (openTag, tagName: string) => {
    const extra = BLOCK_STYLE[tagName.toLowerCase()]
    return extra ? upsertStyle(openTag, extra) : openTag
  })
}

type IsolatedPreviewOptions = {
  background?: string
  color?: string
}

const splitEmailHtmlDocument = (html: string) => {
  if (typeof html !== 'string' || !html)
    return { body: html || '', extraHead: '' }

  const bodyMatch = html.match(/<body[^>]*>([\s\S]*)<\/body>/i)
  if (!bodyMatch)
    return { body: html, extraHead: '' }

  const headMatch = html.match(/<head[^>]*>([\s\S]*)<\/head>/i)
  const extraHead = (headMatch?.[1] || '').replace(/<meta\b[^>]*charset[^>]*>/gi, '')

  return { body: bodyMatch[1], extraHead }
}

/**
 * Wrap outgoing HTML in a blank document so the in-app preview is not affected
 * by Tailwind / dark mode. Fragments stay in the body unchanged. Full email
 * documents are unwrapped so we do not nest <html>/<body>, while keeping
 * their <head> styles. Arial is the canvas default (Gmail / most webmail),
 * not the iframe Times New Roman fallback.
 */
export const wrapEmailHtmlForIsolatedPreview = (html: string, options: IsolatedPreviewOptions = {}) => {
  const background = options.background || '#ffffff'
  const color = options.color || '#000000'
  const { body, extraHead } = splitEmailHtmlDocument(html)

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="color-scheme" content="light only"><style>html,body{margin:0;background:${background};color:${color};font-family:Arial,Helvetica,sans-serif;}</style>${extraHead}</head><body>${body}</body></html>`
}
